<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="dk-header">
        <span class="dk-bar dk-bar--xl"></span>
        <span class="dk-bar dk-bar--md"></span>
      </div>
      <div class="dk-deck">
        <div
          v-for="i in 5"
          :key="i"
          class="dk-card"
          :class="{ '--drawn': i === 5 }"
          :style="{ '--i': i - 1 }"
        >
          <span v-if="i === 5" class="dk-suit">A♠</span>
          <span v-else class="dk-back"></span>
        </div>
      </div>
      <div class="dk-caption">
        <span class="dk-bar dk-bar--md dk-bar--accent"></span>
      </div>
    </template>

    <template #audience>
      <span class="dk-phone-q"></span>
      <div class="dk-mini-deck">
        <span class="dk-mini-card" :style="{ '--i': 0 }"></span>
        <span class="dk-mini-card" :style="{ '--i': 1 }"></span>
        <span class="dk-mini-card" :style="{ '--i': 2 }"></span>
      </div>
      <button type="button" class="dk-draw-btn" tabindex="-1">
        <span class="material-symbols-rounded dk-draw-icon">style</span>
        Draw a card
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewADeckOfCards',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
})
</script>

<style scoped lang="scss">
.dk-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.dk-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); }
.dk-bar--md { width: 40%; }
.dk-bar--accent { background: var(--accent); }

.dk-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.dk-deck {
  position: relative;
  height: 110px;
  display: flex; justify-content: center; align-items: center;
  margin: 8px 0 4px;
}
.dk-card {
  position: absolute;
  width: 60px; height: 90px;
  border-radius: 8px;
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.12);
  box-shadow: 0 2px 6px rgba(15, 15, 20, 0.08);
  display: grid; place-items: center;
  transform: rotate(calc((var(--i) - 2) * 6deg)) translateX(calc((var(--i) - 2) * 8px));
  animation: dk-fan 4s infinite;
  animation-delay: calc(var(--i) * 60ms);
}
.dk-back {
  width: 80%; height: 80%;
  border-radius: 4px;
  background: var(--accent);
  background-image:
    repeating-linear-gradient(45deg, transparent 0 4px, rgba(255, 255, 255, 0.18) 4px 5px);
}
.dk-card.--drawn {
  z-index: 10;
  animation: dk-draw 4s cubic-bezier(0.5, 0, 0.3, 1) infinite;
}
.dk-suit {
  color: var(--accent);
  font-weight: 800;
  font-size: 18px;
}

.dk-caption {
  text-align: center;
  display: flex; justify-content: center;
  margin-top: 2px;
}

/* phone */
.dk-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.dk-mini-deck {
  position: relative;
  height: 70px;
  display: flex; justify-content: center; align-items: center;
}
.dk-mini-card {
  position: absolute;
  width: 38px; height: 54px;
  border-radius: 6px;
  background: var(--accent);
  background-image:
    repeating-linear-gradient(45deg, transparent 0 3px, rgba(255, 255, 255, 0.18) 3px 4px);
  border: 1px solid color-mix(in srgb, var(--accent) 70%, black);
  transform: rotate(calc((var(--i) - 1) * 8deg)) translateX(calc((var(--i) - 1) * 4px));
}
.dk-draw-btn {
  appearance: none;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--accent);
  color: white;
  border: none;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  animation: dk-tap 4s infinite;
}
.dk-draw-icon { font-size: 14px; }

@keyframes dk-fan {
  0%, 30%   { transform: rotate(calc((var(--i) - 2) * 2deg)) translateX(calc((var(--i) - 2) * 3px)); }
  50%, 100% { transform: rotate(calc((var(--i) - 2) * 6deg)) translateX(calc((var(--i) - 2) * 8px)); }
}
@keyframes dk-draw {
  0%, 35%  { transform: translateY(0) translateX(0) rotate(0); opacity: 0; }
  55%      { transform: translateY(-30px) translateX(20px) rotate(8deg); opacity: 1; }
  85%, 100% { transform: translateY(-30px) translateX(20px) rotate(8deg); opacity: 1; }
}
@keyframes dk-tap {
  0%, 4%  { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 50%, transparent); }
  25%     { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%    { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .dk-card, .dk-card.--drawn, .dk-draw-btn { animation: none; }
  .dk-card.--drawn { transform: translateY(-30px) translateX(20px) rotate(8deg); opacity: 1; }
}
</style>
